import React, { useMemo } from "react";
import MyTimePicker from "../../../components/common/TimePicker";

export const DEFAULT_CYCLES = [
  { value: 3, label: "3 day" },
  { value: 7, label: "7 day" },
  { value: 14, label: "14 day" },
  { value: 30, label: "30 day" },
];

const formatTime = (t) => {
  if (!t) return null;
  const d = t instanceof Date ? t : new Date(t);
  return isNaN(d)
    ? String(t)
    : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const PaymentSchedulePicker = ({
  time,
  onTimeChange,
  cycle,
  onCycleChange,
  cycles = DEFAULT_CYCLES,
  timeLabel = "At what time",
  cycleLabel = "Repeat every",
  showSummary = true,
  disabled = false,
  idPrefix = "pay",
}) => {
  const cycleValue = Number(cycle) || null;
  const selectedCycle = useMemo(
    () => cycles.find((c) => c.value === cycleValue),
    [cycles, cycleValue],
  );
  const timeText = formatTime(time);
  const ready = !!(selectedCycle && timeText);
  const labelId = `${idPrefix}-cycle-label`;

  return (
    <div
      className={`mt-4 flex flex-col gap-4 transition-opacity ${
        disabled ? "pointer-events-none opacity-50" : ""
      }`}
    >
      {/* Cycle: segmented control */}
      <div>
        <span
          id={labelId}
          className="mb-1.5 block text-xs font-semibold tracking-wide text-slate-500"
        >
          {cycleLabel}
        </span>
        <div
          role="radiogroup"
          aria-labelledby={labelId}
          className="grid grid-cols-2 gap-1 rounded-xl border border-slate-200 bg-slate-100 p-1 sm:grid-cols-4"
        >
          {cycles.map((c) => {
            const id = `${idPrefix}-cycle-${c.value}`;
            return (
              <React.Fragment key={c.value}>
                <input
                  type="radio"
                  id={id}
                  name={`${idPrefix}-cycle`}
                  className="peer sr-only"
                  checked={cycleValue === c.value}
                  disabled={disabled}
                  onChange={() => onCycleChange(c.value)}
                />
                <label
                  htmlFor={id}
                  className="cursor-pointer select-none rounded-lg px-3 py-2 text-center text-sm font-medium text-slate-500 transition
                    hover:text-slate-900
                    peer-checked:bg-white peer-checked:font-semibold peer-checked:text-slate-900 peer-checked:shadow-sm
                    peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-500 peer-focus-visible:ring-offset-2"
                >
                  {c.label}
                </label>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Time */}
      <div className="max-w-56">
        <MyTimePicker
          selectedTime={time}
          onTimeChange={onTimeChange}
          label={timeLabel}
          disabled={disabled}
        />
      </div>

      {/* Summary */}
      {showSummary && (
        <div
          aria-live="polite"
          className={`flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-sm transition-colors ${
            ready
              ? "bg-emerald-50 text-emerald-800"
              : "bg-slate-100 text-slate-500"
          }`}
        >
          <span
            aria-hidden="true"
            className={`h-2 w-2 flex-none rounded-full ${
              ready ? "bg-emerald-500" : "bg-slate-400/60"
            }`}
          />
          {ready ? (
            <span>
              Payments are collected{" "}
              <strong className="font-semibold">
                every {selectedCycle.label}
              </strong>{" "}
              at <strong className="font-semibold">{timeText}</strong>
            </span>
          ) : (
            <span>Pick a cycle and a time to schedule payments</span>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentSchedulePicker;
